// ═══════════════════════════════════════════════════════════════════════════════
// Main app chrome — top bar + scrollable content + bottom nav
// PWA: inner scroll + document lock. Browser tab: document scroll (Chrome UI auto-hide).
// ═══════════════════════════════════════════════════════════════════════════════

import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { UnpairedEmployerBanner } from '../pairing/UnpairedEmployerBanner';
import { AppTopBar } from './AppTopBar';
import { BottomNav } from './BottomNav';
import { WebPushPermissionPrompt } from '../WebPushPermissionPrompt';
import { AndroidChromePushHintPrompt } from '../AndroidChromePushHintPrompt';
import { useMainAppDocumentLock } from '../../hooks/useMainAppDocumentLock';
import { useIosPwaTextareaKeyboardRecovery } from '../../hooks/useIosPwaTextareaKeyboardRecovery';
import { usePwaInputFocusScroll } from '../../hooks/usePwaInputFocusScroll';
import { usePwaVisibleViewportHeight } from '../../hooks/usePwaVisibleViewportHeight';
import {
  MAIN_APP_SCROLL_BROWSER_CLASS,
  MAIN_APP_SCROLL_PWA_CLASS,
  MAIN_APP_SCROLL_TOP_PADDING_CLASS,
  MAIN_APP_SHELL_BROWSER_CLASS,
  MAIN_APP_SHELL_PWA_CLASS,
} from '../../lib/layoutChrome';
import { isRunningAsPwa } from '../../lib/pwaRuntime';

/** @deprecated Import from `lib/mainAppScroll` */
export { MAIN_APP_SCROLL_SELECTOR } from '../../lib/mainAppScroll';

const SCROLL_EDGE_THRESHOLD_PX = 56;
const TOP_BAR_HIDE_AFTER_SCROLL_PX = 64;
const SCROLL_DELTA_THRESHOLD_PX = 4;

type MainAppShellProps = {
  children: React.ReactNode;
};

type ScrollMetrics = {
  scrollTop: number;
  maxScrollTop: number;
  isNearTop: boolean;
  isNearBottom: boolean;
};

const resolvePwaScrollMetrics = (container: HTMLDivElement | null): ScrollMetrics | null => {
  if (!container) {
    return null;
  }
  const { scrollTop, scrollHeight, clientHeight } = container;
  const maxScrollTop = Math.max(0, scrollHeight - clientHeight);
  return {
    scrollTop,
    maxScrollTop,
    isNearTop: scrollTop <= SCROLL_EDGE_THRESHOLD_PX,
    isNearBottom: scrollTop >= maxScrollTop - SCROLL_EDGE_THRESHOLD_PX,
  };
};

const resolveBrowserScrollMetrics = (): ScrollMetrics => {
  const scrollTop = window.scrollY;
  const scrollHeight = document.documentElement.scrollHeight;
  const clientHeight = window.innerHeight;
  const maxScrollTop = Math.max(0, scrollHeight - clientHeight);
  return {
    scrollTop,
    maxScrollTop,
    isNearTop: scrollTop <= SCROLL_EDGE_THRESHOLD_PX,
    isNearBottom: scrollTop >= maxScrollTop - SCROLL_EDGE_THRESHOLD_PX,
  };
};

/** Page is too short (or already at top) so the user cannot scroll up to reveal the top bar. */
const shouldForceShowTopBar = (metrics: ScrollMetrics): boolean =>
  metrics.maxScrollTop <= SCROLL_EDGE_THRESHOLD_PX || metrics.isNearTop;

export const MainAppShell = ({ children }: MainAppShellProps) => {
  const location = useLocation();
  const usePwaInnerScroll = useMemo(() => isRunningAsPwa(), []);
  const [isTopBarHidden, setIsTopBarHidden] = useState(false);
  const lastScrollYRef = useRef(0);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const hasModalOpenRef = useRef(false);
  const syncTopBarFromLayoutRef = useRef<() => void>(() => {});

  useMainAppDocumentLock(usePwaInnerScroll);
  usePwaVisibleViewportHeight(usePwaInnerScroll);
  usePwaInputFocusScroll(usePwaInnerScroll, scrollContainerRef);
  useIosPwaTextareaKeyboardRecovery(usePwaInnerScroll);

  useEffect(() => {
    if (usePwaInnerScroll) {
      const container = scrollContainerRef.current;
      container?.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      lastScrollYRef.current = container?.scrollTop ?? 0;
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      lastScrollYRef.current = window.scrollY;
    }
    setIsTopBarHidden(false);
  }, [location.pathname, usePwaInnerScroll]);

  useEffect(() => {
    const readMetrics = (): ScrollMetrics | null => {
      if (usePwaInnerScroll) {
        return resolvePwaScrollMetrics(scrollContainerRef.current);
      }
      return resolveBrowserScrollMetrics();
    };

    const revealTopBarIfStuckHidden = (metrics: ScrollMetrics): boolean => {
      if (!shouldForceShowTopBar(metrics)) {
        return false;
      }
      setIsTopBarHidden(false);
      lastScrollYRef.current = metrics.scrollTop;
      return true;
    };

    const syncTopBarFromLayout = (): void => {
      const metrics = readMetrics();
      if (!metrics) {
        return;
      }
      revealTopBarIfStuckHidden(metrics);
    };
    syncTopBarFromLayoutRef.current = syncTopBarFromLayout;

    const applyTopBarFromMetrics = (metrics: ScrollMetrics): void => {
      if (revealTopBarIfStuckHidden(metrics)) {
        return;
      }

      const { scrollTop, isNearBottom } = metrics;
      const deltaY = scrollTop - lastScrollYRef.current;
      const hasMovedEnough = Math.abs(deltaY) >= SCROLL_DELTA_THRESHOLD_PX;

      if (!hasMovedEnough) {
        return;
      }

      if (isNearBottom) {
        setIsTopBarHidden(true);
        lastScrollYRef.current = scrollTop;
        return;
      }

      if (deltaY > 0 && scrollTop > TOP_BAR_HIDE_AFTER_SCROLL_PX) {
        setIsTopBarHidden(true);
      } else if (deltaY < 0) {
        setIsTopBarHidden(false);
      }

      lastScrollYRef.current = scrollTop;
    };

    const handleScroll = () => {
      if (hasModalOpenRef.current) {
        return;
      }
      const metrics = readMetrics();
      if (metrics) {
        applyTopBarFromMetrics(metrics);
      }
    };

    const layoutSyncFrame = { id: 0 };
    const scheduleLayoutSync = (): void => {
      if (layoutSyncFrame.id !== 0) {
        return;
      }
      layoutSyncFrame.id = window.requestAnimationFrame(() => {
        layoutSyncFrame.id = 0;
        syncTopBarFromLayout();
      });
    };

    const container = scrollContainerRef.current;
    container?.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', scheduleLayoutSync);

    const resizeObserver = new ResizeObserver(() => {
      scheduleLayoutSync();
    });
    if (container) {
      resizeObserver.observe(container);
    }
    resizeObserver.observe(document.documentElement);
    const firstChild = container?.firstElementChild;
    if (firstChild instanceof Element) {
      resizeObserver.observe(firstChild);
    }

    scheduleLayoutSync();

    return () => {
      syncTopBarFromLayoutRef.current = () => {};
      container?.removeEventListener('scroll', handleScroll);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', scheduleLayoutSync);
      resizeObserver.disconnect();
      if (layoutSyncFrame.id !== 0) {
        window.cancelAnimationFrame(layoutSyncFrame.id);
      }
    };
  }, [usePwaInnerScroll]);

  useEffect(() => {
    const previousBodyOverflow = document.body.style.overflow;
    const previousBodyTouchAction = document.body.style.touchAction;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    const container = scrollContainerRef.current;
    const previousContainerOverflow = container?.style.overflow ?? '';
    const previousContainerTouchAction = container?.style.touchAction ?? '';
    const previousContainerOverscrollBehavior = container?.style.overscrollBehavior ?? '';

    const syncModalScrollLock = () => {
      const hasModal = document.querySelector('[aria-modal="true"]') !== null;
      hasModalOpenRef.current = hasModal;
      if (hasModal) {
        document.body.style.overflow = 'hidden';
        document.body.style.touchAction = 'none';
        document.documentElement.style.overflow = 'hidden';
        if (usePwaInnerScroll && container) {
          container.style.overflow = 'hidden';
          container.style.touchAction = 'none';
          container.style.overscrollBehavior = 'none';
        }
        syncTopBarFromLayoutRef.current();
        return;
      }
      document.body.style.overflow = previousBodyOverflow;
      document.body.style.touchAction = previousBodyTouchAction;
      document.documentElement.style.overflow = previousHtmlOverflow;
      if (container) {
        container.style.overflow = previousContainerOverflow;
        container.style.touchAction = previousContainerTouchAction;
        container.style.overscrollBehavior = previousContainerOverscrollBehavior;
      }
      syncTopBarFromLayoutRef.current();
    };

    const observer = new MutationObserver(() => {
      syncModalScrollLock();
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-modal', 'class'],
    });
    syncModalScrollLock();

    return () => {
      observer.disconnect();
      hasModalOpenRef.current = false;
      document.body.style.overflow = previousBodyOverflow;
      document.body.style.touchAction = previousBodyTouchAction;
      document.documentElement.style.overflow = previousHtmlOverflow;
      if (container) {
        container.style.overflow = previousContainerOverflow;
        container.style.touchAction = previousContainerTouchAction;
        container.style.overscrollBehavior = previousContainerOverscrollBehavior;
      }
    };
  }, [usePwaInnerScroll]);

  const shellClassName = usePwaInnerScroll ? MAIN_APP_SHELL_PWA_CLASS : MAIN_APP_SHELL_BROWSER_CLASS;
  const scrollClassName = usePwaInnerScroll
    ? `${MAIN_APP_SCROLL_PWA_CLASS} ${MAIN_APP_SCROLL_TOP_PADDING_CLASS}`
    : MAIN_APP_SCROLL_BROWSER_CLASS;

  const unpairedBannerStickyTopClass = isTopBarHidden
    ? 'top-[var(--fdh-play-banner-h,0px)]'
    : 'top-[calc(56px+env(safe-area-inset-top,0px)+var(--fdh-play-banner-h,0px))]';

  return (
    <div className={shellClassName}>
      <AppTopBar isHidden={isTopBarHidden} />
      <AndroidChromePushHintPrompt />
      <WebPushPermissionPrompt />
      <div ref={scrollContainerRef} data-main-app-scroll className={scrollClassName}>
        {location.pathname === '/pairing/status' || location.pathname === '/pairing/generate-code' ? null : (
          <div className={`sticky z-10 transition-[top] duration-200 ease-out ${unpairedBannerStickyTopClass}`}>
            <UnpairedEmployerBanner />
          </div>
        )}
        {children}
      </div>
      <BottomNav usePwaLayout={usePwaInnerScroll} />
    </div>
  );
};
